import { create } from 'zustand';

import { askNlq } from './nlq-api';

/**
 * Session-local Ask BankIQ conversation store (SCRUM-83, plan D6). Mirrors the
 * v7 prototype's module-level state (`nlqThread` / `nlqRecentQuestions` /
 * `nlqIsLoading`): the thread and recents survive page navigation within the
 * session (zustand stores live outside the React tree, so AppShell's
 * `key={role}` remounts don't reset them) and reset on a full reload. There is
 * deliberately NO server-side session persistence in the MVP — every AI call
 * is already audited by the gateway; NlqSession storage is reserved for
 * SCRUM-87.
 *
 * Answers come from the REAL /api/intelligence/nlq/ask endpoint (SCRUM-85):
 * the prototype's simulated 1.2–2.4s delay is replaced by actual watsonx
 * latency. Answers are never cached (React Query is not used for the ask) —
 * each question is a fresh grounded call.
 */

export interface NlqUserMessage {
  role: 'user';
  text: string;
}

export interface NlqAiMessage {
  role: 'ai';
  /** GitHub-flavored markdown, rendered via NlqMarkdown (never raw HTML). */
  answerMarkdown: string;
  confidence: 'high' | 'medium' | 'low';
  /** Server-derived source line; '' hides the source row (fallback anatomy). */
  source: string;
  followups: string[];
  /** false = honest fallback (gateway unavailable / unusable model output). */
  aiGenerated: boolean;
}

export type NlqMessage = NlqUserMessage | NlqAiMessage;

/**
 * One session conversation (SCRUM-701). `title` is the first user question,
 * trimmed + truncated; `messages` is that conversation's full thread. The
 * active conversation's messages are ALSO mirrored to the top-level
 * `messages` field so existing consumers/tests keep working unchanged.
 */
export interface NlqConversation {
  id: string;
  title: string;
  messages: NlqMessage[];
  createdAt: number;
  updatedAt: number;
}

/** Cap on stored recent questions (welcome state shows the first 4). */
export const RECENTS_STORED_CAP = 8;

/** Cap on stored conversations (newest-first) — bounds sessionStorage size (RT4). */
export const CONVERSATIONS_STORED_CAP = 20;

/** Max characters of a conversation title (first question), before an ellipsis (RT6). */
export const CONVERSATION_TITLE_MAX = 40;

/** Derives a conversation title from the first question: trimmed, truncated, non-empty. */
export function deriveConversationTitle(question: string): string {
  const trimmed = question.trim();
  if (trimmed === '') return 'New chat';
  return trimmed.length > CONVERSATION_TITLE_MAX
    ? `${trimmed.slice(0, CONVERSATION_TITLE_MAX - 1).trimEnd()}…`
    : trimmed;
}

/**
 * Recents persist to sessionStorage (SCRUM-84 decision): in the prototype both
 * the thread and recents were plain JS module state, which made the welcome
 * state's "Recent questions" list unreachable in practice — recents only render
 * on an empty thread, and the thread never resets within a session. Persisting
 * ONLY the recents (the thread stays in-memory) makes the feature real: a tab
 * reload lands on the welcome state with your recent questions. Still
 * session-scoped (sessionStorage), still no server-side storage (plan D6).
 * Manual try/catch helpers per the auth roles.ts sessionStorage precedent.
 */
const RECENTS_STORAGE_KEY = 'bankiq-nlq-recents';

/** Reads persisted recents; [] when absent/invalid/unavailable (privacy mode). */
export function readPersistedRecents(): string[] {
  try {
    const raw = window.sessionStorage.getItem(RECENTS_STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((q): q is string => typeof q === 'string' && q.trim() !== '')
      .slice(0, RECENTS_STORED_CAP);
  } catch {
    return [];
  }
}

function persistRecents(recents: string[]): void {
  try {
    window.sessionStorage.setItem(RECENTS_STORAGE_KEY, JSON.stringify(recents));
  } catch {
    // sessionStorage unavailable — recents still work for this render tree.
  }
}

/**
 * Conversations persist to sessionStorage too (SCRUM-701): session-scoped chat
 * history, no server storage (plan D6; SCRUM-87 reserved). Same manual try/catch
 * posture as recents. `activeId` is persisted alongside so a reload re-opens the
 * conversation you were in — validated against the stored list on read (RT7).
 */
const CONVERSATIONS_STORAGE_KEY = 'bankiq-nlq-conversations';

function isNlqMessage(v: unknown): v is NlqMessage {
  if (typeof v !== 'object' || v === null) return false;
  const r = (v as { role?: unknown }).role;
  return r === 'user' || r === 'ai';
}

interface PersistedConversations {
  conversations: NlqConversation[];
  activeId: string | null;
}

/** Reads persisted conversations; empty when absent/invalid/unavailable. Validates activeId (RT7). */
export function readPersistedConversations(): PersistedConversations {
  const empty: PersistedConversations = { conversations: [], activeId: null };
  try {
    const raw = window.sessionStorage.getItem(CONVERSATIONS_STORAGE_KEY);
    if (!raw) return empty;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return empty;

    const rawConvos = (parsed as { conversations?: unknown }).conversations;
    if (!Array.isArray(rawConvos)) return empty;

    const conversations = rawConvos
      .filter((c): c is NlqConversation => {
        if (typeof c !== 'object' || c === null) return false;
        const o = c as Record<string, unknown>;
        return (
          typeof o.id === 'string' &&
          typeof o.title === 'string' &&
          Array.isArray(o.messages) &&
          o.messages.every(isNlqMessage) &&
          typeof o.createdAt === 'number' &&
          typeof o.updatedAt === 'number'
        );
      })
      .slice(0, CONVERSATIONS_STORED_CAP);

    const rawActive = (parsed as { activeId?: unknown }).activeId;
    // Orphan-guard (RT7): only honor an activeId that still exists in the list.
    const activeId =
      typeof rawActive === 'string' && conversations.some((c) => c.id === rawActive)
        ? rawActive
        : null;

    return { conversations, activeId };
  } catch {
    return empty;
  }
}

function persistConversations(conversations: NlqConversation[], activeId: string | null): void {
  try {
    window.sessionStorage.setItem(
      CONVERSATIONS_STORAGE_KEY,
      JSON.stringify({ conversations: conversations.slice(0, CONVERSATIONS_STORED_CAP), activeId }),
    );
  } catch {
    // sessionStorage unavailable / quota — conversations still work in-memory (RT4).
  }
}

/** New conversation id — crypto.randomUUID with a Math.random fallback for older/test envs. */
function newConversationId(): string {
  try {
    return crypto.randomUUID();
  } catch {
    return `c-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  }
}

/**
 * Client-side fallback answer (plan RT3): rendered for the server's
 * `aiGenerated:false` envelope AND for transport errors. Ports the prototype's
 * NLQ_FALLBACK copy to markdown — low confidence, NO source line.
 */
export const NLQ_FALLBACK_MARKDOWN =
  "I wasn't able to find a confident answer to that question based on the available data.\n\n" +
  'This could be because:\n\n' +
  '- The data needed may not be included in the current data snapshot\n' +
  '- The question may require a more specific framing — try mentioning a metric, time period, or segment\n' +
  '- Some analysis types require a manual model run by your team\n\n' +
  'Here are some things I *can* help with:\n\n' +
  '**Customer insights** — segment breakdown, balances, relationship tiers\n\n' +
  '**Campaign results** — campaign counts and status mix\n\n' +
  '**Referral pipeline** — referral volume, status, and conversions\n\n' +
  // SCRUM-842: say "staff users" explicitly. "Active users" read as customers, and the
  // underlying snapshot figure counts bank employees who log into BankIQ, not customers.
  '**Platform activity** — active staff users and data freshness';

const FALLBACK_FOLLOWUPS = [
  'How many customers do we serve?',
  'Show campaign performance',
  'How are deposits trending?',
];

function fallbackMessage(): NlqAiMessage {
  return {
    role: 'ai',
    answerMarkdown: NLQ_FALLBACK_MARKDOWN,
    confidence: 'low',
    source: '',
    followups: [...FALLBACK_FOLLOWUPS],
    aiGenerated: false,
  };
}

interface NlqState {
  /**
   * The ACTIVE conversation's thread, mirrored at the top level so existing
   * consumers/tests (`useNlqStore(s => s.messages)`) keep working unchanged.
   * Empty when there is no active conversation (welcome state).
   */
  messages: NlqMessage[];
  /** All session conversations, newest-activity-first (SCRUM-701). */
  conversations: NlqConversation[];
  /** The open conversation id; null = fresh welcome (no active conversation). */
  activeId: string | null;
  /** Most-recent-first, deduplicated, capped at RECENTS_STORED_CAP. */
  recents: string[];
  isLoading: boolean;
  /**
   * The full ask flow (prototype `nlqAsk`): recents dedup+unshift → user
   * bubble renders immediately → loading → real API call → AI message (or the
   * honest fallback on failure). Ignored while a question is in flight
   * (prototype `nlqIsLoading` guard). Appends to the ACTIVE conversation,
   * creating one (titled from the first question) when none is active.
   */
  /**
   * SCRUM-1022: `detokenize` restores real customer names from the snapshot's
   * opaque CUST_&lt;id&gt; tokens BEFORE the answer is stored/shown, so watsonx
   * never saw a name but the user does. The page supplies it from the cached
   * /api/customers dataset; omitted (e.g. in tests) means no substitution.
   */
  ask: (question: string, detokenize?: (text: string) => string) => Promise<void>;
  /**
   * Reset to the welcome state (SCRUM-701): clears the active thread WITHOUT
   * deleting any stored conversation. No-op while loading. Does NOT itself
   * create a conversation — a new one is only stored on the next `ask` (RT3).
   */
  newChat: () => void;
  /** Switch the active conversation to <id> (loads its thread). No-op if unknown or while loading. */
  openConversation: (id: string) => void;
  /**
   * Delete a conversation from the session/store. If it was active, falls back
   * to the welcome state (activeId=null, empty thread). No-op while loading.
   */
  deleteConversation: (id: string) => void;
}

const initialConversations = readPersistedConversations();

/** Returns the active conversation's messages (or [] when none) for the mirror field. */
function messagesFor(conversations: NlqConversation[], activeId: string | null): NlqMessage[] {
  if (activeId === null) return [];
  return conversations.find((c) => c.id === activeId)?.messages ?? [];
}

export const useNlqStore = create<NlqState>((set, get) => ({
  conversations: initialConversations.conversations,
  activeId: initialConversations.activeId,
  messages: messagesFor(initialConversations.conversations, initialConversations.activeId),
  recents: readPersistedRecents(),
  isLoading: false,

  ask: async (question: string, detokenize?: (text: string) => string) => {
    const trimmed = question.trim();
    // SCRUM-1022: fail-open — with no de-tokenizer, text passes through unchanged
    // (tokens would remain visible, but the answer is never blank or broken).
    const dt = detokenize ?? ((t: string) => t);
    if (!trimmed || get().isLoading) return;

    // Recents dedup+unshift (unchanged surface). Compute-then-set: the storage
    // write stays out of the state updater, which zustand expects to be pure.
    const recents = [trimmed, ...get().recents.filter((q) => q !== trimmed)].slice(
      0,
      RECENTS_STORED_CAP,
    );
    persistRecents(recents);

    // Resolve the TARGET conversation up front and capture its id (RT2): the
    // answer must land in THIS conversation even if the user switches while the
    // question is in flight. Create a new conversation when none is active (RT3:
    // a conversation is only stored on the first ask, never on an empty newChat).
    const now = Date.now();
    let targetId = get().activeId;
    if (targetId === null) {
      targetId = newConversationId();
      const created: NlqConversation = {
        id: targetId,
        title: deriveConversationTitle(trimmed),
        messages: [{ role: 'user', text: trimmed }],
        createdAt: now,
        updatedAt: now,
      };
      const conversations = [created, ...get().conversations].slice(0, CONVERSATIONS_STORED_CAP);
      persistConversations(conversations, targetId);
      set({
        isLoading: true,
        conversations,
        activeId: targetId,
        messages: created.messages,
        recents,
      });
    } else {
      const conversations = get().conversations.map((c) =>
        c.id === targetId
          ? {
              ...c,
              messages: [...c.messages, { role: 'user' as const, text: trimmed }],
              updatedAt: now,
            }
          : c,
      );
      persistConversations(conversations, get().activeId);
      set((s) => ({
        isLoading: true,
        conversations,
        messages: messagesFor(conversations, s.activeId),
        recents,
      }));
    }

    let answer: NlqAiMessage;
    try {
      const res = await askNlq(trimmed);
      answer = res.aiGenerated
        ? {
            role: 'ai',
            // SCRUM-1022: de-tokenize before store/display so the persisted
            // conversation (sessionStorage) holds real names and survives reload.
            answerMarkdown: dt(res.answerMarkdown),
            confidence: res.confidence,
            source: dt(res.source),
            followups: res.followups.slice(0, 3).map(dt),
            aiGenerated: true,
          }
        : fallbackMessage();
    } catch {
      // Transport / HTTP / shape failure — same honest fallback (never blank,
      // never an error screen; the SCRUM-236 principle applied to a thread UI).
      answer = fallbackMessage();
    }

    // Append the answer to the TARGET conversation (captured above), even if the
    // user has since switched/started a new chat (RT2). The mirrored `messages`
    // reflects whatever is active NOW.
    set((s) => {
      const conversations = s.conversations.map((c) =>
        c.id === targetId ? { ...c, messages: [...c.messages, answer], updatedAt: Date.now() } : c,
      );
      persistConversations(conversations, s.activeId);
      return {
        isLoading: false,
        conversations,
        messages: messagesFor(conversations, s.activeId),
      };
    });
  },

  newChat: () => {
    if (get().isLoading) return; // don't reset mid-flight (RT2)
    persistConversations(get().conversations, null);
    set({ activeId: null, messages: [] });
  },

  openConversation: (id: string) => {
    // No-op mid-flight: switching away while a question is in flight would leave
    // the newly-opened conversation showing the global loading spinner even
    // though it has nothing pending. Same guard `newChat` uses (gap analysis).
    if (get().isLoading) return;
    const exists = get().conversations.some((c) => c.id === id);
    if (!exists) return;
    persistConversations(get().conversations, id);
    set((s) => ({ activeId: id, messages: messagesFor(s.conversations, id) }));
  },

  deleteConversation: (id: string) => {
    // No-op mid-flight: deleting the in-flight conversation would silently drop
    // its pending answer, and deleting another leaves loading state confusing.
    // Same guard `newChat`/`openConversation` use (gap analysis).
    if (get().isLoading) return;
    set((s) => {
      const conversations = s.conversations.filter((c) => c.id !== id);
      const activeId = s.activeId === id ? null : s.activeId;
      persistConversations(conversations, activeId);
      return { conversations, activeId, messages: messagesFor(conversations, activeId) };
    });
  },
}));
